// Site Induction builder — section navigator + editors + draft/publish/revisions.
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSafetyNavigate as useNavigate } from "@/lib/safetyNavigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Save, Send, Plus, Trash2, Loader2, CheckCircle2, Circle, AlertTriangle, GitBranch, Sparkles, Eye, Users, PlayCircle, QrCode, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SECTION_DEFS = [
  { id: "site_information", label: "Site Information", type: "fields", fields: [["site_name", "Site name"], ["job_number", "Job number"], ["site_address", "Site address"], ["client", "Client"], ["project_manager", "Project manager"], ["site_supervisor", "Site supervisor"], ["site_contact", "Site contact"], ["working_hours", "Working hours"]] },
  { id: "welcome", label: "Welcome", type: "fields", area: true, fields: [["body", "Site introduction"], ["scope", "Scope of works"], ["access", "Site access"], ["parking", "Parking"], ["restricted_areas", "Restricted areas"]] },
  { id: "emergency", label: "Emergency", type: "fields", critical: true, fields: [["emergency_contact", "Emergency contact *"], ["muster_point", "Muster point *"], ["first_aid_location", "First aid location"], ["first_aiders", "First aiders"], ["fire_equipment", "Fire equipment"], ["emergency_access", "Emergency access"], ["nearest_medical", "Nearest medical facility"], ["evacuation", "Evacuation procedure"]] },
  { id: "site_rules", label: "Site Rules", type: "list", item: [["title", "Rule"]] },
  { id: "hazards", label: "Hazards & Controls", type: "list", item: [["name", "Hazard"], ["controls", "Controls"]] },
  { id: "ppe", label: "PPE", type: "chips" },
  { id: "plant_traffic", label: "Plant & Traffic", type: "fields", area: true, fields: [["body", "Traffic, plant routes, exclusion zones, spotters…"]] },
  { id: "hazardous_substances", label: "Hazardous Substances", type: "info" },
  { id: "environment", label: "Environment", type: "fields", area: true, fields: [["body", "Spill response, sediment, dust, noise, waste…"]] },
  { id: "reporting", label: "Reporting", type: "fields", area: true, fields: [["body", "How to report hazards, incidents and near misses on this site"]] },
  { id: "knowledge_check", label: "Knowledge Check", type: "knowledge" },
  { id: "declaration", label: "Declaration", type: "fields", area: true, fields: [["text", "Declaration text"]] },
];

export default function SiteInductionBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [content, setContent] = useState(null);
  const [active, setActive] = useState("site_information");
  const [saving, setSaving] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [view, setView] = useState("build");
  const [aiBusy, setAiBusy] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/site-inductions/${id}`);
    setData(data);
    setContent(JSON.parse(JSON.stringify(data.current_revision?.content || {})));
  };
  useEffect(() => { load().catch(() => toast.error("Could not load induction")); }, [id]);

  const rev = data?.current_revision;
  const isDraft = rev?.status === "DRAFT";
  const setSection = (sid, val) => setContent((c) => ({ ...c, [sid]: val }));

  const save = async () => {
    setSaving(true);
    try { await api.patch(`/site-inductions/revisions/${rev.id}`, { content }); toast.success("Saved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const publish = async (force) => {
    try {
      await api.patch(`/site-inductions/revisions/${rev.id}`, { content });
      await api.post(`/site-inductions/revisions/${rev.id}/publish`, { force });
      toast.success(`Published REV ${String(rev.revision_number).padStart(2, "0")}`);
      setPublishOpen(false); await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.missing) { toast.error(`Missing: ${d.missing.join(", ")}`); }
      else toast.error(d || "Publish failed");
    }
  };
  const newRevision = async () => {
    try { const { data } = await api.post(`/site-inductions/${id}/new-revision`, {}); toast.success(`Created REV ${String(data.revision_number).padStart(2, "0")}`); await load(); setActive("site_information"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const aiDraft = async () => {
    setAiBusy(true);
    try {
      await api.patch(`/site-inductions/revisions/${rev.id}`, { content });
      const { data } = await api.post(`/site-inductions/${id}/ai-draft`, {});
      setContent(JSON.parse(JSON.stringify(data.content || {})));
      toast.success("AI filled the empty sections — review before publishing");
    } catch (e) { toast.error(e.response?.data?.detail || "AI draft failed"); }
    finally { setAiBusy(false); }
  };

  const done = useMemo(() => {
    const s = {};
    if (!content) return s;
    SECTION_DEFS.forEach((d) => {
      const v = content[d.id];
      if (d.type === "list" || d.type === "chips") s[d.id] = (v || []).length > 0;
      else if (d.type === "info") s[d.id] = true;
      else if (d.type === "knowledge") s[d.id] = v?.enabled ? (v.questions || []).length > 0 : true;
      else s[d.id] = Object.values(v || {}).some((x) => String(x || "").trim());
      if (d.critical) s[d.id] = !!(v?.muster_point && v?.emergency_contact);
    });
    return s;
  }, [content]);

  if (!data || !content) return <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>;
  const def = SECTION_DEFS.find((d) => d.id === active);

  return (
    <div className="pb-10" data-testid="si-builder">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <button onClick={() => nav("/app/safety/site-inductions")} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1 mb-1" data-testid="si-back"><ArrowLeft className="w-3.5 h-3.5" /> Site Inductions</button>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] font-bold bg-[#09090B] text-[#FF5F15] px-1.5 py-0.5 rounded">{data.induction.job_number}</span>
            <h1 className="text-xl font-black tracking-tight">{data.induction.site_name}</h1>
            <span className="font-mono text-sm text-zinc-400">REV {String(rev.revision_number).padStart(2, "0")}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${rev.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : rev.status === "DRAFT" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-500"}`}>{rev.status}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {rev.status === "ACTIVE" && (
            <Button variant="outline" onClick={() => nav(`/app/safety/site-inductions/${id}/induct`)} className="h-9 rounded-lg text-[11px] font-bold uppercase" data-testid="si-open-runner"><PlayCircle className="w-4 h-4 mr-1.5" /> Open induction</Button>
          )}
          {rev.status === "ACTIVE" && (
            <Button variant="outline" onClick={() => setQrOpen(true)} className="h-9 rounded-lg text-[11px] font-bold uppercase" data-testid="si-visitor-qr"><QrCode className="w-4 h-4 mr-1.5" /> Visitor QR</Button>
          )}
          {isDraft ? (
            <>
              <Button variant="outline" onClick={aiDraft} disabled={aiBusy} className="h-9 rounded-lg text-[11px] font-bold uppercase border-[#FF5F15]/40 text-[#E65100]" data-testid="si-ai-draft">{aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Draft with AI</>}</Button>
              <Button variant="outline" onClick={save} disabled={saving} className="h-9 rounded-lg text-[11px] font-bold uppercase" data-testid="si-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button>
              <Button onClick={() => setPublishOpen(true)} className="h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase" data-testid="si-publish"><Send className="w-4 h-4 mr-1.5" /> Publish</Button>
            </>
          ) : (
            <Button variant="outline" onClick={newRevision} className="h-9 rounded-lg text-[11px] font-bold uppercase" data-testid="si-new-revision"><GitBranch className="w-4 h-4 mr-1.5" /> New revision</Button>
          )}
        </div>
      </div>

      <div className="inline-flex rounded-lg border border-zinc-200 bg-white p-0.5 mb-4" data-testid="si-view-switch">
        {[["build", "Build"], ["preview", "Preview", Eye], ["records", `Records${data.completion_count ? ` (${data.completion_count})` : ""}`, Users]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`si-view-${k}`}
            className={`px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${view === k ? "bg-[#09090B] text-white" : "text-zinc-500 hover:text-zinc-800"}`}>
            {Icon && <Icon className="w-3.5 h-3.5" />}{label}
          </button>
        ))}
      </div>

      {view === "preview" ? (
        <InductionPreview content={content} induction={data.induction} />
      ) : view === "records" ? (
        <RecordsPanel completions={data.completions || []} />
      ) : (
      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        <div className="lg:sticky lg:top-4 self-start">
          <div className="rounded-xl border border-zinc-200 bg-white p-1.5 flex lg:flex-col gap-0.5 overflow-x-auto" data-testid="si-section-nav">
            {SECTION_DEFS.map((d, i) => {
              const st = done[d.id];
              return (
                <button key={d.id} onClick={() => setActive(d.id)} data-testid={`si-sec-${d.id}`}
                  className={`shrink-0 flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left ${active === d.id ? "bg-[#FF5F15]/10 text-[#FF5F15] font-bold" : "hover:bg-zinc-50 text-zinc-600"}`}>
                  {st ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-zinc-300 shrink-0" />}
                  <span className="tabular-nums text-[10px] text-zinc-400 w-4">{i + 1}</span>{d.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-zinc-200 bg-white p-5 min-h-[400px]" data-testid="si-editor">
          <h2 className="font-black text-lg mb-4">{def.label}</h2>
          {!isDraft && <div className="mb-4 text-[11px] text-zinc-500 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> This revision is {rev.status.toLowerCase()} and read-only. Create a new revision to make changes.</div>}
          <SectionEditor def={def} value={content[def.id]} onChange={(v) => setSection(def.id, v)} readOnly={!isDraft} />
        </div>
      </div>
      )}

      {publishOpen && (
        <Dialog open onOpenChange={(v) => { if (!v) setPublishOpen(false); }}>
          <DialogContent className="rounded-xl max-w-md" data-testid="si-publish-dialog">
            <DialogHeader><DialogTitle className="font-black uppercase tracking-tight">Publish site induction</DialogTitle></DialogHeader>
            <div className="text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-zinc-500">Site</span><span className="font-bold">{data.induction.site_name}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Revision</span><span className="font-bold">REV {String(rev.revision_number).padStart(2, "0")}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Sections complete</span><span className="font-bold">{Object.values(done).filter(Boolean).length} / {SECTION_DEFS.length}</span></div>
            </div>
            <p className="text-xs text-zinc-400">This makes this revision the active induction. Emergency contact and muster point are required.</p>
            <DialogFooter><Button variant="outline" onClick={() => setPublishOpen(false)}>Cancel</Button><Button onClick={() => publish(false)} className="bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="si-confirm-publish">Publish REV {String(rev.revision_number).padStart(2, "0")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {qrOpen && <QrDialog id={id} onClose={() => setQrOpen(false)} />}
    </div>
  );
}

function QrDialog({ id, onClose }) {
  const [link, setLink] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/site-inductions/${id}/public-link`);
      setLink(data);
      const img = await api.get(`/site-inductions/${id}/public-qr.png`, { responseType: "blob" });
      setQrUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(img.data); });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load link"); }
  };
  useEffect(() => { load(); return () => { setQrUrl((u) => { if (u) URL.revokeObjectURL(u); return null; }); }; }, [id]); // eslint-disable-line

  const copy = () => { if (link?.url) { navigator.clipboard?.writeText(link.url); toast.success("Link copied"); } };
  const rotate = async () => {
    if (!window.confirm("Generate a new link? The old QR/link will stop working.")) return;
    setBusy(true);
    try { await api.post(`/site-inductions/${id}/public-link/rotate`, {}); await load(); toast.success("New link generated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/site-inductions/${id}/public-link/toggle`, { enabled: !(link?.enabled) }); setLink((l) => ({ ...l, enabled: data.enabled })); toast.success(data.enabled ? "Link enabled" : "Link disabled"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl max-w-md" data-testid="si-qr-dialog">
        <DialogHeader><DialogTitle className="font-black uppercase tracking-tight flex items-center gap-2"><QrCode className="w-5 h-5 text-[#FF5F15]" /> Visitor sign-on QR</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-500">Print this and put it at the site entrance. Visitors, cartage drivers and subbies scan it and complete the induction on their own phone — no login needed.</p>
        {!link ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>
        ) : (
          <>
            <div className="grid place-items-center py-2">
              {qrUrl ? <img src={qrUrl} alt="Induction QR" className="w-52 h-52 rounded-lg border border-zinc-200" data-testid="si-qr-img" /> : <Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" />}
            </div>
            {!link.enabled && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 text-center">This link is currently disabled — visitors can't sign on.</div>}
            <div className="flex items-center gap-2">
              <Input readOnly value={link.url || ""} className="rounded-lg h-9 text-xs" data-testid="si-qr-url" />
              <Button size="sm" variant="outline" onClick={copy} className="h-9 rounded-lg shrink-0" data-testid="si-qr-copy"><Copy className="w-4 h-4" /></Button>
            </div>
            <DialogFooter className="gap-2 sm:justify-between">
              <Button variant="outline" onClick={toggle} disabled={busy} className="h-9 rounded-lg text-[11px] font-bold uppercase" data-testid="si-qr-toggle">{link.enabled ? "Disable link" : "Enable link"}</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={rotate} disabled={busy} className="h-9 rounded-lg text-[11px] font-bold uppercase" data-testid="si-qr-rotate"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> New link</Button>
                <a href={qrUrl || "#"} download={`induction-qr.png`}><Button className="h-9 rounded-lg bg-[#09090B] text-white text-[11px] font-bold uppercase" data-testid="si-qr-download">Download</Button></a>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const fmtDT = (d) => { try { return new Date(d).toLocaleString("en-NZ", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const PTYPE_LABEL = { worker: "Worker", visitor: "Visitor", cartage: "Cartage", subcontractor: "Subbie" };

function InductionPreview({ content, induction }) {
  const c = content || {};
  const order = [
    ["welcome", "Welcome"], ["emergency", "Emergency"], ["site_rules", "Site Rules"],
    ["hazards", "Hazards & Controls"], ["ppe", "Required PPE"], ["plant_traffic", "Plant & Traffic"],
    ["environment", "Environment"], ["reporting", "Reporting"],
  ];
  const has = (k, v) => (k === "ppe" || k === "site_rules" || k === "hazards")
    ? Array.isArray(v) && v.length > 0
    : v && typeof v === "object" && Object.values(v).some((x) => String(x || "").trim());
  const si = c.site_information || {};
  const siRows = [["Site", si.site_name], ["Address", si.site_address], ["Client", si.client], ["Project manager", si.project_manager], ["Site supervisor", si.site_supervisor], ["Working hours", si.working_hours]].filter(([, v]) => String(v || "").trim());
  const kc = c.knowledge_check || {};
  return (
    <div className="max-w-2xl" data-testid="si-preview">
      <div className="rounded-2xl bg-[#09090B] text-white p-6 mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF5F15] mb-1">Worker preview</div>
        <h1 className="text-2xl font-black tracking-tight leading-tight">{induction.site_name}</h1>
        <div className="text-sm text-zinc-300 mt-1">{induction.job_number}</div>
      </div>
      {siRows.length > 0 && (
        <PreviewCard title="Site information">
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {siRows.map(([k, v]) => <div key={k}><dt className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{k}</dt><dd className="font-medium">{v}</dd></div>)}
          </dl>
        </PreviewCard>
      )}
      {order.filter(([k]) => has(k, c[k])).map(([k, label]) => (
        <PreviewCard key={k} title={label}>
          {k === "ppe" ? (
            <div className="flex flex-wrap gap-2">{(c[k] || []).map((p, i) => <span key={i} className="px-3 py-1.5 rounded-full bg-[#FF5F15]/10 text-[#E65100] text-sm font-bold">{p}</span>)}</div>
          ) : k === "site_rules" ? (
            <ul className="space-y-1.5">{(c[k] || []).map((r, i) => <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {r.title || r}</li>)}</ul>
          ) : k === "hazards" ? (
            <div className="space-y-2">{(c[k] || []).map((h, i) => <div key={i} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3"><div className="font-bold text-sm text-amber-900">{h.name}</div>{h.controls && <div className="text-sm text-zinc-700 mt-0.5">{h.controls}</div>}</div>)}</div>
          ) : (
            <div className="space-y-2">{Object.entries(c[k] || {}).filter(([, v]) => String(v || "").trim()).map(([kk, v]) => <div key={kk} className="text-sm text-zinc-700 whitespace-pre-wrap">{v}</div>)}</div>
          )}
        </PreviewCard>
      ))}
      {kc.enabled && (kc.questions || []).length > 0 && (
        <PreviewCard title={`Knowledge check (${(kc.questions || []).length})`}>
          <ol className="list-decimal ml-5 space-y-1.5 text-sm">{(kc.questions || []).map((q, i) => <li key={i}>{q.q}</li>)}</ol>
        </PreviewCard>
      )}
      <PreviewCard title="Declaration">
        <p className="text-sm text-zinc-700 whitespace-pre-wrap">{(c.declaration || {}).text}</p>
      </PreviewCard>
    </div>
  );
}
const PreviewCard = ({ title, children }) => (
  <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4"><h3 className="font-black text-base mb-3">{title}</h3>{children}</div>
);

function RecordsPanel({ completions }) {
  if (!completions.length) {
    return <div className="rounded-xl border-2 border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500" data-testid="si-records-empty">No one has completed this induction yet. Publish it, then share the induction link with workers.</div>;
  }
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-x-auto" data-testid="si-records">
      <table className="w-full text-sm min-w-[680px]">
        <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
          <th className="py-2.5 px-4">Name</th><th className="px-3">Type</th><th className="px-3">Company</th><th className="px-3">Rev</th><th className="px-3">Knowledge</th><th className="px-3">Signed</th><th className="px-3">Status</th>
        </tr></thead>
        <tbody>
          {completions.map((c) => (
            <tr key={c.id} className="border-b border-zinc-100 last:border-0" data-testid={`si-record-${c.id}`}>
              <td className="py-2.5 px-4 font-bold">{c.participant_name}</td>
              <td className="px-3 text-zinc-500">{PTYPE_LABEL[c.participant_type] || "Worker"}</td>
              <td className="px-3 text-zinc-500">{c.company || "—"}</td>
              <td className="px-3 font-mono">REV {String(c.revision_number || 1).padStart(2, "0")}</td>
              <td className="px-3">{c.knowledge_result ? `${c.knowledge_result.correct}/${c.knowledge_result.total}` : "—"}</td>
              <td className="px-3 text-zinc-500">{fmtDT(c.completed_at)}</td>
              <td className="px-3">{c.is_current ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase">Current</span> : <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">Re-induct</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function SectionEditor({ def, value, onChange, readOnly }) {
  const v = value || {};
  if (def.type === "info") {
    return <div className="rounded-lg border-2 border-dashed border-zinc-200 p-6 text-sm text-zinc-500">Hazardous substances assigned to this job will appear here for selection once the Hazardous Substances module is linked (Phase 4). Workers will be able to view Safety Info and the SDS.</div>;
  }
  if (def.type === "fields") {
    return (
      <div className="space-y-3">
        {def.fields.map(([k, label]) => (
          <div key={k}>
            <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
            {def.area
              ? <Textarea rows={3} disabled={readOnly} value={v[k] || ""} onChange={(e) => onChange({ ...v, [k]: e.target.value })} className="rounded-lg text-sm" data-testid={`si-f-${def.id}-${k}`} />
              : <Input disabled={readOnly} value={v[k] || ""} onChange={(e) => onChange({ ...v, [k]: e.target.value })} className="rounded-lg h-9" data-testid={`si-f-${def.id}-${k}`} />}
          </div>
        ))}
      </div>
    );
  }
  if (def.type === "list") {
    const arr = Array.isArray(value) ? value : [];
    const upd = (i, patch) => onChange(arr.map((x, j) => j === i ? { ...x, ...patch } : x));
    return (
      <div className="space-y-2">
        {arr.map((item, i) => (
          <div key={i} className="rounded-lg border border-zinc-200 p-3 space-y-2" data-testid={`si-item-${def.id}-${i}`}>
            {def.item.map(([k, label]) => (
              <div key={k}><label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
                <Input disabled={readOnly} value={item[k] || ""} onChange={(e) => upd(i, { [k]: e.target.value })} className="rounded-lg h-9" /></div>
            ))}
            {!readOnly && <button onClick={() => onChange(arr.filter((_, j) => j !== i))} className="text-red-600 text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
          </div>
        ))}
        {!readOnly && <Button size="sm" variant="outline" onClick={() => onChange([...arr, {}])} className="rounded-lg text-[11px] font-bold" data-testid={`si-add-${def.id}`}><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>}
      </div>
    );
  }
  if (def.type === "chips") {
    return <ChipsEditor value={value} onChange={onChange} readOnly={readOnly} />;
  }
  if (def.type === "knowledge") {
    const kc = value || { enabled: false, questions: [] };
    const qs = kc.questions || [];
    const setKc = (patch) => onChange({ ...kc, ...patch });
    const updQ = (i, patch) => setKc({ questions: qs.map((q, j) => j === i ? { ...q, ...patch } : q) });
    return (
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" disabled={readOnly} checked={!!kc.enabled} onChange={(e) => setKc({ enabled: e.target.checked })} data-testid="si-kc-enabled" /> Require a knowledge check</label>
        {kc.enabled && (
          <>
            {qs.map((q, i) => (
              <div key={i} className="rounded-lg border border-zinc-200 p-3 space-y-2">
                <Input disabled={readOnly} value={q.q || ""} onChange={(e) => updQ(i, { q: e.target.value })} placeholder="Question" className="rounded-lg h-9" />
                <Input disabled={readOnly} value={q.answer || ""} onChange={(e) => updQ(i, { answer: e.target.value })} placeholder="Expected answer / acknowledgement" className="rounded-lg h-9" />
                {!readOnly && <button onClick={() => setKc({ questions: qs.filter((_, j) => j !== i) })} className="text-red-600 text-xs flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
              </div>
            ))}
            {!readOnly && <Button size="sm" variant="outline" onClick={() => setKc({ questions: [...qs, {}] })} className="rounded-lg text-[11px] font-bold" data-testid="si-add-question"><Plus className="w-3.5 h-3.5 mr-1" /> Add question</Button>}
          </>
        )}
      </div>
    );
  }
  return null;
}

function ChipsEditor({ value, onChange, readOnly }) {
  const arr = Array.isArray(value) ? value : [];
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onChange([...arr, val.trim()]); setVal(""); } };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {arr.map((c, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-sm font-medium">{c}{!readOnly && <button onClick={() => onChange(arr.filter((_, j) => j !== i))}>×</button>}</span>
        ))}
        {arr.length === 0 && <span className="text-sm text-zinc-400">No PPE added yet.</span>}
      </div>
      {!readOnly && (
        <div className="flex gap-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. Hard hat" className="rounded-lg h-9 max-w-xs" data-testid="si-ppe-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <Button size="sm" variant="outline" onClick={add} className="rounded-lg text-[11px] font-bold" data-testid="si-ppe-add">Add</Button>
        </div>
      )}
    </div>
  );
}
